"use client";

import { emailSchema } from "@repo/api/lib/validation";
import { authClient } from "@repo/auth/client";
import { getAuthErrorMessage } from "@saas/auth/client";
import { AuthHeading } from "@saas/shared/components/AuthWrapper";
import { useForm, useStore } from "@tanstack/react-form";
import { Link, useSearch } from "@tanstack/react-router";
import { Button } from "@ui/components/button";
import {
	Field,
	FieldDescription,
	FieldError,
	FieldLabel,
} from "@ui/components/field";
import { Input } from "@ui/components/input";
import { Spinner } from "@ui/components/spinner";
import { useState } from "react";
import { AuthError, authErrorInput, visibleFieldError } from "../AuthNotice";

interface SignupStepEmailProps {
	email: string;
	/** The invitation fixes the email: it must match to join the team. */
	locked: boolean;
	organizationName?: string;
	onSent: (email: string) => void;
}

export function SignupStepEmail({
	email,
	locked,
	organizationName,
	onSent,
}: SignupStepEmailProps) {
	const [rootError, setRootError] = useState<string | null>(null);
	const searchParams = useSearch({ strict: false }) as {
		invitationId?: string;
	};

	const form = useForm({
		defaultValues: { email },
		onSubmit: async ({ value }) => {
			setRootError(null);
			try {
				// "sign-in" is the only OTP type that sends to users who don't
				// exist yet; the others skip silently.
				const { error } = await authClient.emailOtp.sendVerificationOtp(
					{
						email: value.email,
						type: "sign-in",
					},
				);
				if (error) {
					throw error;
				}
				onSent(value.email);
			} catch (error) {
				setRootError(getAuthErrorMessage(authErrorInput(error)));
			}
		},
	});

	const isSubmitting = useStore(form.store, (state) => state.isSubmitting);
	const attempted = useStore(
		form.store,
		(state) => state.submissionAttempts > 0,
	);

	return (
		<div className="flex flex-col">
			<AuthHeading
				className="mb-6"
				title={
					organizationName
						? `Join ${organizationName}`
						: "Create your account"
				}
				description="We'll send a 6-digit code to confirm it."
			/>
			<form
				className="flex flex-col gap-5"
				noValidate
				onSubmit={(event) => {
					event.preventDefault();
					event.stopPropagation();
					form.handleSubmit();
				}}
			>
				<AuthError message={rootError} />
				{rootError?.toLowerCase().includes("already exists") ? (
					<p className="-mt-2 text-sm">
						<Link
							to="/login"
							search={{ ...searchParams, email }}
							className="font-medium underline underline-offset-4"
						>
							Sign in instead
						</Link>
					</p>
				) : null}
				<form.Field name="email" validators={{ onBlur: emailSchema }}>
					{(field) => {
						const error = visibleFieldError(field, attempted);
						const invalid = !!error;
						return (
							<Field data-invalid={invalid || undefined}>
								<FieldLabel htmlFor="email">
									Work email
								</FieldLabel>
								<Input
									id="email"
									type="email"
									value={field.state.value}
									onChange={(event) =>
										field.handleChange(event.target.value)
									}
									onBlur={field.handleBlur}
									readOnly={locked}
									autoComplete="email"
									autoFocus={!locked}
									aria-invalid={invalid || undefined}
									className={
										locked
											? "text-muted-foreground"
											: undefined
									}
								/>
								{locked ? (
									<FieldDescription>
										From your invitation.
									</FieldDescription>
								) : null}
								{invalid ? <FieldError errors={error} /> : null}
							</Field>
						);
					}}
				</form.Field>
				<Button type="submit" size="lg" disabled={isSubmitting}>
					{isSubmitting ? <Spinner data-icon="inline-start" /> : null}
					Send me a code
				</Button>
				<p className="text-center text-muted-foreground text-sm">
					Already have an account?{" "}
					<Link
						to="/login"
						search={searchParams}
						className="font-medium text-foreground underline-offset-4 hover:underline"
					>
						Sign in
					</Link>
				</p>
			</form>
		</div>
	);
}
